import { api } from './baseApi';
import type {
  ProviderDocument,
  ProviderDocumentResponse,
  ProviderDocumentsResponse,
} from '../../types/providerDocument';

type ProviderKind = 'specialists' | 'consultants';

const basePath = (kind: ProviderKind, providerId: number) =>
  `/dashboard/${kind}/${providerId}/documents/`;

export const providerDocumentsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getProviderDocuments: builder.query<
      ProviderDocumentsResponse,
      { kind: ProviderKind; providerId: number }
    >({
      query: ({ kind, providerId }) => basePath(kind, providerId),
      providesTags: (_result, _error, { kind, providerId }) => [
        { type: 'ProviderDocuments', id: `${kind}-${providerId}` },
      ],
    }),
    createProviderDocument: builder.mutation<
      ProviderDocumentResponse,
      { kind: ProviderKind; providerId: number; formData: FormData }
    >({
      query: ({ kind, providerId, formData }) => ({
        url: basePath(kind, providerId),
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: (_result, _error, { kind, providerId }) => [
        { type: 'ProviderDocuments', id: `${kind}-${providerId}` },
        kind === 'specialists' ? 'Specialist' : 'Consultant',
      ],
    }),
    updateProviderDocument: builder.mutation<
      ProviderDocumentResponse,
      {
        kind: ProviderKind;
        providerId: number;
        documentId: number;
        formData: FormData;
      }
    >({
      query: ({ kind, providerId, documentId, formData }) => ({
        url: `${basePath(kind, providerId)}${documentId}/`,
        method: 'PATCH',
        body: formData,
      }),
      invalidatesTags: (_result, _error, { kind, providerId }) => [
        { type: 'ProviderDocuments', id: `${kind}-${providerId}` },
      ],
    }),
    deleteProviderDocument: builder.mutation<
      { success: boolean },
      { kind: ProviderKind; providerId: number; documentId: number }
    >({
      query: ({ kind, providerId, documentId }) => ({
        url: `${basePath(kind, providerId)}${documentId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { kind, providerId }) => [
        { type: 'ProviderDocuments', id: `${kind}-${providerId}` },
      ],
    }),
  }),
});

export const {
  useGetProviderDocumentsQuery,
  useCreateProviderDocumentMutation,
  useUpdateProviderDocumentMutation,
  useDeleteProviderDocumentMutation,
} = providerDocumentsApi;

export type { ProviderDocument };
